import withEvents from 'analytics/withEvents';
import { FadeWrapper } from 'components/authentication/styled';
import { setActiveInsight } from 'store/state/insightsContext/actions';
import { IStoryEventChangeMeta } from 'components/view-pager-to-stories';
import { ShowAt } from 'consts/breakpoints';
import React, { useLayoutEffect, useState, useCallback } from 'react';
import styled from '@emotion/styled';
import { getInfiniteNextIndex, getInfinitePrevIndex } from 'utils/gallery';
import { HighlightDesktop } from './HighlightDesktop';
import { HighlightsMobile } from './HighlightsMobile';
import HighlightsPagerControls from './HighlightsPagerControls';
import { IHighlight } from './types';
import { State, RootAction } from 'store/state';
import { activeInsightIdSelector } from 'store/state/insightsContext/selectors';
import { connect } from 'react-redux';
import {
  ISendFeedbackVariables,
  MutationType,
} from 'store/sagas/apiService/types';
import { Dispatch } from 'redux';
import { inlineMutate } from 'store/state/mutationsResponse/actions';
import { breakpointToContentWidth } from 'ds/components/Layout';
import { BreakpointValue } from 'consts/breakpoints';
import { marketplaceSelector } from 'store/state/selectors/router';
import { MarketplaceType } from 'utils/entities';
import { HighlightDesktopNew } from './HighlightDesktopNew';
import { LISTING_PAGE_CONTAINER_WIDTH } from 'screens/ListingPage/styled';


const ExpandedHighlightsPageDesktop = styled.div<{ newDesign?: boolean }>`
  ${({ newDesign }) => `
    height: 100%;
    width: 100%;
    position: relative;
    padding: ${newDesign ? 0 : '84px 0 100px'};
  `}
`;

const HighlightsPagerControlsWrapperNew = styled.div<{ flexEnd: boolean }>`${({ theme, flexEnd }) => `
  position: sticky;
  display: flex;
  border-top: 1px solid ${theme.colors.neutrals.grey9};
  background: ${theme.colors.neutrals.white};
  bottom: 0;
  height: 56px;
  margin-right: calc(((100vw - (min(${LISTING_PAGE_CONTAINER_WIDTH}px, 100vw) - 120px)) / 2 - 32px) * -1);
  padding-right: calc(((100vw - (min(${LISTING_PAGE_CONTAINER_WIDTH}px, 100vw) - 120px)) / 2) - 24px);
  margin-top: 40px;
  border-bottom: 1px solid ${theme.colors.neutrals.grey8};
`}`;


const HighlightsPagerControlsWrapper = styled.div<{ flexEnd: boolean; }>`
  position: fixed;
  display: flex;
  bottom: 0;
  ${({ flexEnd }) => flexEnd ? 'justify-content: flex-end;' : ''}
  ${({ theme }) => theme.isRTL ? 'right' : 'left'}: 0;
  ${({ theme }) => Object.keys(breakpointToContentWidth).map(key => {
    return theme.media.showAt({ at: (+key as BreakpointValue) }) `
      width: ${breakpointToContentWidth[key]};
    `;
  })}
  height: 56px;
  border-top: 1px solid ${({ theme }) => theme.colors.neutrals.grey9};
  background: ${({ theme }) => theme.colors.neutrals.white};
`;

interface ExpandedHighlightsPageProps {
  id?: string;
  activeInsightId?: string;
  setActiveInsightId?: (id: string) => void;
  highlights: IHighlight[];
  onActiveInsightChangeNotify?: (meta?: IStoryEventChangeMeta) => void;
  marketplace: MarketplaceType;
  newDesign?: boolean;
}

const ExpandedHighlightsPage: React.FC<ExpandedHighlightsPageProps> = (props) => {
  const {
    highlights,
    activeInsightId,
    setActiveInsightId,
    onActiveInsightChangeNotify,
    marketplace,
    newDesign,
  } = props;

  const [ isTransitioning, setIsTransitioning ] = useState(false);
  const activeHighlightIndex = highlights.findIndex(({ id: ID }) => activeInsightId === ID);

  const setActiveHighlightIndex = (index: number) => {
    const insightId = highlights[index].id;
    if (index !== activeHighlightIndex) {
      onActiveInsightChangeNotify({
        insightId,
        direction: index > activeHighlightIndex ? 'right' : 'left',
        index,
        total: highlights.length,
      });
    }
    setActiveInsightId(insightId);
  };

  useLayoutEffect(() => {
    setIsTransitioning(true);
    setTimeout(() => setIsTransitioning(false), 200);
  }, [ activeHighlightIndex ]);

  const resetActiveInsightId = useCallback(() => setActiveInsightId(null), [ setActiveInsightId ]);

  const handlePrevIndex = () => setActiveHighlightIndex(getInfinitePrevIndex(activeHighlightIndex, highlights.length));
  const handleNextIndex = () => setActiveHighlightIndex(getInfiniteNextIndex(activeHighlightIndex, highlights.length));
  const handleChangeIndex = (nextIndex: number) => setActiveHighlightIndex(nextIndex);
  const highlightsExist = highlights.length > 1;
  const isCommercial = marketplace === MarketplaceType.Commercial;

  const PagerWrapper = newDesign ? HighlightsPagerControlsWrapperNew : HighlightsPagerControlsWrapper;

  return (
    <div>
      <ShowAt from={3}>
        <ExpandedHighlightsPageDesktop newDesign={newDesign}>
          <FadeWrapper isVisible={!isTransitioning} transitionTime={100}>
            {newDesign ? (
              <HighlightDesktopNew
                isTransitioning={isTransitioning}
                resetActiveInsightId={resetActiveInsightId}
                {...highlights[activeHighlightIndex]}
              />
            ) : (
              <HighlightDesktop
                isTransitioning={isTransitioning}
                {...highlights[activeHighlightIndex]}
              />
            )}
          </FadeWrapper>
          <PagerWrapper
            flexEnd={!highlightsExist}
            data-auto="insight-controllers-root"
          >
            {highlightsExist ? (
              <HighlightsPagerControls
                highlights={highlights}
                activeHighlightIndex={activeHighlightIndex}
                onNextIndex={handleNextIndex}
                onPrevIndex={handlePrevIndex}
                isCommercial={isCommercial}
              />
            ) : null}
          </PagerWrapper>
        </ExpandedHighlightsPageDesktop>
      </ShowAt>
      <ShowAt to={2}>
        <HighlightsMobile
          activeHighlightIndex={activeHighlightIndex}
          highlights={highlights}
          onChangeIndex={handleChangeIndex}
          isCommercial={isCommercial}
        />
      </ShowAt>
    </div>
  );
};


const ExpandedHighlightsPageWithEvents = withEvents<ExpandedHighlightsPageProps>(sendEvent => ({
  onActiveInsightChangeNotify: (meta) => sendEvent('insight_scroll', 'insight', {
    event: {
      direction: meta.direction,
      insight_index: meta.index,
      insight_index_total: meta.total,
      scroll_type: 'manual',
      view_mode: 'expanded',
    },
    insight: {
      insight_id: meta.insightId,
    },
  }),
}))(ExpandedHighlightsPage);

const mapStateToProps = (state: State) => ({
  activeInsightId: activeInsightIdSelector(state),
  marketplace: marketplaceSelector(state),
});

const mapDispatchToProps = (dispatch: Dispatch<RootAction>) => ({
  setActiveInsightId: (id: string) => dispatch(setActiveInsight(id)),
  sendFeedback: (data: ISendFeedbackVariables) => dispatch(inlineMutate(MutationType.SendFeedback, data, data.insightRecordId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ExpandedHighlightsPageWithEvents);
